'use client';

import type { ComponentPropsWithoutRef, RefObject } from 'react';
import { cn } from '../../lib/utils';
import { SanityImage } from '../../sanity/utils/components';
import type { SanityImageProps } from '../../sanity/utils/fragments/image-fragment';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';

interface ColorSwatchProps extends ComponentPropsWithoutRef<'button'> {
  hexColor?: string | null;
  image?: SanityImageProps | null;
  label: string;
  isSelected?: boolean;
  size?: 'sm' | 'md' | 'lg';
  tooltipContainer?: HTMLElement | RefObject<HTMLElement | null> | null;
}

export function ColorSwatch({
  hexColor,
  image,
  label,
  isSelected = false,
  size = 'md',
  tooltipContainer,
  className,
  ...props
}: ColorSwatchProps) {
  const sizeClasses = {
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-10 w-10',
  };

  const imageSizes = {
    sm: { width: 24, height: 24, className: 'h-6 w-6' },
    md: { width: 30, height: 30, className: 'h-[30px] w-[30px]' },
    lg: { width: 38, height: 38, className: 'h-[38px] w-[38px]' },
  };

  const selectedImageSizes = {
    sm: { className: 'h-5 w-5' },
    md: { className: 'h-7 w-7' },
    lg: { className: 'h-9 w-9' },
  };

  return (
    <TooltipProvider>
      <Tooltip delay={0}>
        <TooltipTrigger
          className={cn(
            'flex shrink-0 items-center justify-center overflow-hidden rounded-full hf:border-2 hf:border-primary p-0',
            sizeClasses[size],
            isSelected ? 'border-2 border-primary' : 'border border-gray-200',
            className
          )}
          aria-label={label}
          {...props}
        >
          {image && (
            <SanityImage
              image={image}
              width={imageSizes[size].width}
              height={imageSizes[size].height}
              noLQIP
              className={cn(
                'shrink-0 rounded-full object-cover',
                isSelected
                  ? selectedImageSizes[size].className
                  : imageSizes[size].className
              )}
            />
          )}
          {!image && hexColor && (
            <span
              className={cn(
                'block shrink-0 rounded-full',
                isSelected
                  ? selectedImageSizes[size].className
                  : imageSizes[size].className
              )}
              style={{
                backgroundColor: hexColor,
              }}
              aria-label={label}
            />
          )}
        </TooltipTrigger>
        <TooltipContent sideOffset={10} container={tooltipContainer}>
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
